/**
 * AdminAuditLogPage — Admin kullanıcılarının yazma işlemlerinin okunur kaydı.
 *
 * - Her satır bir eylemin "insan dilinde" özetini + teknik detaylarını gösterir
 * - Varsayılan: sadece oturum açmış kullanıcılar (anonim gürültü gizli)
 * - Yol, kullanıcı, metod, status, IP, cihaz hepsi filtrelenebilir
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, RefreshCw, Activity, Users as UsersIcon, Trash2, Smartphone, Monitor, Info } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import Pagination from "@/components/Pagination";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const METHOD_CLS = {
    POST: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300",
    PUT: "bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300",
    PATCH: "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
    DELETE: "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300",
};

const METHOD_VERB = {
    POST: { tr: "oluşturdu", icon: "➕" },
    PUT: { tr: "güncelledi", icon: "✏️" },
    PATCH: { tr: "düzenledi", icon: "🔧" },
    DELETE: { tr: "SİLDİ", icon: "🗑️" },
};

const ROLE_LABEL = {
    super_admin: "Süper Admin",
    admin: "Admin",
    operations: "Operasyon",
    finance: "Finans",
    finance_approver: "Finans Onay",
    content: "İçerik",
    otel_owner: "Otel Sahibi",
    otel_manager: "Otel Yöneticisi",
    otel_reception: "Resepsiyon",
    otel_accounting: "Otel Muhasebe",
};

/**
 * Translate a REST path into a human-readable Turkish description.
 * Patterns are tried top-down; the first match wins.
 */
const RESOURCE_PATTERNS = [
    // Bookings
    [/^\/api\/admin\/bookings\/[^/]+\/send-email$/, "Rezervasyon maili gönderdi"],
    [/^\/api\/admin\/bookings\/[^/]+\/hotel-confirm$/, "Otel onay durumu değiştirdi"],
    [/^\/api\/admin\/bookings\/[^/]+\/rights-reserved$/, "Rezervasyonu Hakkı Saklı yaptı"],
    [/^\/api\/admin\/bookings\/[^/]+\/refund-request$/, "Rezervasyon için iade talebi oluşturdu"],
    [/^\/api\/admin\/bookings\/[^/]+\/payments$/, "Rezervasyona ödeme ekledi"],
    [/^\/api\/admin\/bookings\/[^/]+\/status$/, "Rezervasyon durumunu değiştirdi"],
    [/^\/api\/admin\/bookings\/[^/]+$/, "Rezervasyon düzenledi"],
    [/^\/api\/admin\/bookings$/, "Yeni rezervasyon oluşturdu"],
    [/^\/api\/bookings$/, "Rezervasyon oluşturuldu"],
    [/^\/api\/bookings\/[^/]+$/, "Rezervasyon kayıtlandı"],

    // Hotels
    [/^\/api\/admin\/hotels\/[^/]+\/room-types$/, "Oda tipi işlemi yaptı"],
    [/^\/api\/admin\/hotels\/[^/]+\/publish$/, "Oteli yayınladı/yayından kaldırdı"],
    [/^\/api\/admin\/hotels\/[^/]+$/, "Otel bilgilerini düzenledi"],
    [/^\/api\/admin\/hotels$/, "Yeni otel oluşturdu"],

    // Rate plans
    [/^\/api\/admin\/rate-plans\/[^/]+$/, "Fiyat planı düzenledi"],
    [/^\/api\/admin\/rate-plans$/, "Yeni fiyat planı oluşturdu"],

    // Destinations
    [/^\/api\/admin\/destinations\/seed-defaults$/, "81 ili destinasyon olarak yükledi"],
    [/^\/api\/admin\/destinations\/[^/]+$/, "Destinasyonu düzenledi"],
    [/^\/api\/admin\/destinations$/, "Yeni destinasyon oluşturdu"],

    // Hotel applications
    [/^\/api\/admin\/hotel-applications\/[^/]+\/approve$/, "Otel başvurusunu ONAYLADI"],
    [/^\/api\/admin\/hotel-applications\/[^/]+\/reject$/, "Otel başvurusunu REDDETTİ"],
    [/^\/api\/admin\/hotel-applications\/[^/]+\/convert-to-hotel$/, "Başvuruyu otele dönüştürdü"],
    [/^\/api\/hotel-applications$/, "Yeni otel başvurusu gönderildi"],

    // Users
    [/^\/api\/admin\/users\/[^/]+\/role$/, "Kullanıcı rolünü değiştirdi"],
    [/^\/api\/admin\/users\/[^/]+$/, "Kullanıcı bilgisi güncelledi"],
    [/^\/api\/admin\/users$/, "Yeni kullanıcı oluşturdu"],

    // Finance
    [/^\/api\/admin\/finance\/refunds\/[^/]+\/approve$/, "İade talebini ONAYLADI"],
    [/^\/api\/admin\/finance\/refunds\/[^/]+\/reject$/, "İade talebini REDDETTİ"],
    [/^\/api\/admin\/finance\/refunds$/, "Yeni iade talebi"],

    // Reviews
    [/^\/api\/admin\/reviews\/[^/]+\/approve$/, "Değerlendirmeyi yayınladı"],
    [/^\/api\/admin\/reviews\/[^/]+\/reject$/, "Değerlendirmeyi reddetti"],

    // CMS/Site
    [/^\/api\/admin\/site-content$/, "Site içeriğini güncelledi"],
    [/^\/api\/admin\/cms-pages$/, "CMS sayfası oluşturdu"],
    [/^\/api\/admin\/ads$/, "Reklam/duyuru oluşturdu"],
    [/^\/api\/admin\/coupons$/, "Kupon oluşturdu"],
    [/^\/api\/admin\/newsletter$/, "Newsletter işlemi"],

    // Support chat
    [/^\/api\/support\/tickets$/, "Yeni destek talebi açtı"],
    [/^\/api\/support\/tickets\/[^/]+\/messages$/, "Destek mesajı gönderdi"],
    [/^\/api\/support\/tickets\/[^/]+$/, "Destek talebini güncelledi (durum/atama)"],
    [/^\/api\/support\/uploads$/, "Destek sohbetine dosya yükledi"],
    [/^\/api\/auth\/switch-hotel$/, "Aktif oteli değiştirdi"],

    // Auth
    [/^\/api\/auth\/login$/, "Sisteme giriş yaptı"],
    [/^\/api\/auth\/logout$/, "Sistemden çıkış yaptı"],
    [/^\/api\/auth\/register$/, "Yeni hesap oluşturdu"],
    [/^\/api\/auth\/password\/reset$/, "Şifre sıfırladı"],
    [/^\/api\/auth\/2fa/, "İki aşamalı doğrulama"],

    // Feature flags
    [/^\/api\/admin\/feature-flags\/[^/]+$/, "Özelliği açtı/kapattı"],

    // Hotel self-service
    [/^\/api\/hotel\/[^/]+$/, "Otel paneli işlemi"],
    [/^\/api\/h\/[^/]+$/, "Otel paneli işlemi"],
];

function describe(path, method) {
    for (const [re, label] of RESOURCE_PATTERNS) {
        if (re.test(path)) return label;
    }
    // Fallback: strip /api/admin/ prefix and prettify
    const seg = path.replace(/^\/api(\/admin)?\//, "").split("/")[0];
    const verb = METHOD_VERB[method]?.tr || "işlem yaptı";
    if (seg) return `${seg} ${verb}`;
    return "İşlem yaptı";
}

function statusCls(code) {
    if (!code) return "text-slate-500";
    if (code >= 500) return "text-red-600 font-bold";
    if (code >= 400) return "text-amber-600 font-bold";
    if (code >= 300) return "text-sky-600";
    return "text-emerald-600";
}

function deviceFromUA(ua) {
    if (!ua) return { label: "—", icon: Monitor };
    if (/iPhone|iPod|Android.*Mobile/.test(ua)) return { label: "Mobil", icon: Smartphone };
    if (/iPad|Tablet/.test(ua)) return { label: "Tablet", icon: Smartphone };
    return { label: "Masaüstü", icon: Monitor };
}

export default function AdminAuditLogPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ user_email: "", method: "", q: "", days: 7, only_authed: true });
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/login");
        load();
        // eslint-disable-next-line
    }, [user, authLoading, filters.days, filters.user_email, filters.method, filters.only_authed, page]);

    useEffect(() => { setPage(1); }, [filters.user_email, filters.method, filters.days, filters.q, filters.only_authed]);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.user_email) params.set("user_email", filters.user_email);
            if (filters.method) params.set("method", filters.method);
            if (filters.q) params.set("q", filters.q);
            if (filters.only_authed) params.set("only_authed", "true");
            params.set("days", String(filters.days));
            params.set("limit", String(PAGE_SIZE));
            params.set("skip", String((page - 1) * PAGE_SIZE));
            const r = await api.get(`/admin/audit-logs?${params.toString()}`);
            setItems(r.data.items || []);
            setUsers(r.data.users || []);
            setTotal(r.data.total || 0);
        } finally { setLoading(false); }
    };

    const cleanup = async () => {
        if (!window.confirm("30 günden eski anonim analitik/kota olaylarını kalıcı olarak silmek ister misiniz? Bu kayıtlar hem denetim için gereksiz hem Analitik modülünde zaten mevcut.")) return;
        try {
            const r = await api.delete("/admin/audit-logs/cleanup-noise?days=30");
            toast.success(`${r.data.deleted} kayıt temizlendi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Hata");
        }
    };

    return (
        <AdminLayout
            title="Denetim Kaydı"
            action={
                <div className="flex gap-2">
                    <button onClick={cleanup} data-testid="audit-cleanup" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-100 dark:bg-rose-950/40 hover:bg-rose-200 text-rose-700 dark:text-rose-300 text-xs font-bold border border-rose-200 dark:border-rose-900">
                        <Trash2 className="h-3.5 w-3.5" /> Eski Gürültüyü Temizle
                    </button>
                    <button onClick={load} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-200 dark:bg-slate-800 text-xs font-bold">
                        <RefreshCw className="h-3.5 w-3.5" /> Yenile
                    </button>
                </div>
            }
        >
            {/* Help banner */}
            <div className="mb-3 p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 flex items-start gap-2">
                <Info className="h-4 w-4 text-sky-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-sky-900 dark:text-sky-200">
                    <b>Denetim Kaydı</b>, admin ve otel kullanıcılarının gerçekleştirdiği <b>yazma</b> işlemlerini kaydeder (yeni oluştur, güncelle, sil).
                    Her satır eylemin insan dilinde özetini gösterir; satıra tıklayınca teknik ayrıntıları (yol, cihaz, HTTP metodu) görürsünüz.
                </div>
            </div>

            {/* Filter bar */}
            <div className="flex flex-wrap gap-2 mb-4 p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <input data-testid="audit-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Yol veya kullanıcı..." className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                </div>
                <select value={filters.user_email} onChange={(e) => setFilters({ ...filters, user_email: e.target.value })} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value="">Tüm kullanıcılar</option>
                    {users.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value="">Tüm metodlar</option>
                    <option value="POST">Yeni Oluştur (POST)</option>
                    <option value="PUT">Güncelle (PUT)</option>
                    <option value="PATCH">Düzenle (PATCH)</option>
                    <option value="DELETE">Sil (DELETE)</option>
                </select>
                <select value={filters.days} onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value, 10) })} className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm">
                    <option value={1}>Son 24 saat</option>
                    <option value={7}>Son 7 gün</option>
                    <option value={30}>Son 30 gün</option>
                    <option value={90}>Son 90 gün</option>
                </select>
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-bold cursor-pointer">
                    <input type="checkbox" checked={filters.only_authed} onChange={(e) => setFilters({ ...filters, only_authed: e.target.checked })} className="h-4 w-4 accent-sky-500" />
                    <UsersIcon className="h-3.5 w-3.5" /> Sadece oturum açmış
                </label>
                <span className="text-xs text-slate-500 ml-auto self-center">{total} kayıt</span>
            </div>

            {loading ? (
                <div className="space-y-1">{[...Array(8)].map((_, i) => <div key={i} className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />)}</div>
            ) : items.length === 0 ? (
                <div className="p-10 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
                    <Activity className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 text-sm">Bu aralıkta denetim kaydı yok.</p>
                </div>
            ) : (
                <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="audit-table">
                        {items.map((r, i) => {
                            const action = describe(r.path, r.method);
                            const verb = METHOD_VERB[r.method] || { icon: "·" };
                            const dev = deviceFromUA(r.user_agent);
                            const DevIcon = dev.icon;
                            const isOpen = expanded === r.id;
                            return (
                                <li key={r.id} className="group">
                                    <button
                                        onClick={() => setExpanded(isOpen ? null : r.id)}
                                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 flex items-center gap-3"
                                    >
                                        <span className="text-xl flex-shrink-0" aria-hidden>{verb.icon}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-slate-900 dark:text-white text-sm">{action}</span>
                                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${METHOD_CLS[r.method] || ""}`}>{r.method}</span>
                                                <span className={`text-[11px] ${statusCls(r.status_code)}`}>{r.status_code}</span>
                                            </div>
                                            <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                                                <span className="font-medium">{r.user_email || <i className="text-slate-400">anonim ziyaretçi</i>}</span>
                                                {r.user_role && <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[9px] font-bold uppercase">{ROLE_LABEL[r.user_role] || r.user_role}</span>}
                                                <span className="text-slate-400">·</span>
                                                <span data-testid={`audit-time-${r.id || i}`}>{r.timestamp ? new Date(r.timestamp).toLocaleString("tr-TR", { timeZone: "Europe/Istanbul", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : ""}</span>
                                                <span className="text-slate-400">·</span>
                                                <DevIcon className="h-3 w-3" /> <span>{dev.label}</span>
                                                {r.ip && <><span className="text-slate-400">·</span><span className="font-mono text-[10px]">{r.ip}</span></>}
                                            </div>
                                        </div>
                                        <span className="text-slate-400 text-xs">{isOpen ? "▲" : "▼"}</span>
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-4 pt-0 bg-slate-50 dark:bg-slate-800/30 text-xs">
                                            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 pl-11">
                                                <div><dt className="inline text-slate-500 font-bold">Yol: </dt><dd className="inline font-mono break-all">{r.path}</dd></div>
                                                <div><dt className="inline text-slate-500 font-bold">Metod: </dt><dd className="inline">{r.method}</dd></div>
                                                <div><dt className="inline text-slate-500 font-bold">Status: </dt><dd className="inline">{r.status_code}</dd></div>
                                                <div><dt className="inline text-slate-500 font-bold">IP: </dt><dd className="inline font-mono">{r.ip || "—"}</dd></div>
                                                <div className="md:col-span-2 truncate"><dt className="inline text-slate-500 font-bold">Tarayıcı: </dt><dd className="inline">{r.user_agent || "—"}</dd></div>
                                            </dl>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                    <Pagination total={total} page={page} pageSize={PAGE_SIZE} onChange={setPage} label="kayıt" />
                </div>
            )}
        </AdminLayout>
    );
}
